import { FileText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../api/client.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';
import type { Dodatek, Smlouva, Zakazka, ZmenaSeznam } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { PodpisVstup } from '../components/PodpisVstup.js';
import { PodpisProtistranyDialog } from '../components/PodpisProtistranyDialog.js';
import { formatDatum, formatKc, souctiCastky } from '../lib/format.js';
import { ZMENA_STAV_LABEL } from '../lib/labels.js';
import {
  Badge,
  Btn,
  Chyba,
  Dialog,
  EmptyState,
  Karta,
  Nacitani,
  Napoveda,
  Select,
  SkeletonSeznam,
  td,
  th,
} from '../components/ui.js';

/**
 * DODATKY — obálky nad podepsanými výnosovými ZL jedné smlouvy (07 §9).
 * Podpis dodatku je JEDINÁ cesta ke stavu fakturovatelná (tvrdá brána,
 * invariant 5) a navyšuje cenu Díla → přepočet pozastávek.
 */
export function DodatkyPage() {
  const { maPravo } = useAuth();
  const navigateSeznam = useNavigate();
  const [hledani, setHledani] = useSearchParams();
  // Zařadit do dodatku z detailu Změny (B6.1): ?novy=1&zakazka=…&zl=… otevře dialog.
  const [zakladam, setZakladam] = useState(hledani.get('novy') === '1');
  const initialZakazkaId = hledani.get('zakazka') ?? '';
  const initialZlId = hledani.get('zl') ?? '';

  // Seznam se řídí globálním přepínačem zakázky; „všechny" na jedno kliknutí.
  const { data: zakazkySeznam } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  const { vybranaId } = useVybranaZakazka(zakazkySeznam);
  const [vsechnyZakazky, setVsechnyZakazky] = useState(false);
  useEffect(() => setVsechnyZakazky(false), [vybranaId]);
  const filtr = !vsechnyZakazky && vybranaId ? `?zakazkaId=${vybranaId}` : '';
  const {
    data: dodatky,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dodatky', filtr],
    queryFn: () => api.get<Dodatek[]>(`/api/dodatky${filtr}`),
  });

  const zavri = () => {
    setZakladam(false);
    if (hledani.has('novy')) setHledani({}, { replace: true });
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">Dodatky</h1>
        {maPravo('finance.write') && (
          <Btn size="desktop" onClick={() => setZakladam(true)}>
            Nový dodatek
          </Btn>
        )}
      </div>

      {isLoading && <SkeletonSeznam radku={4} />}
      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}

      {/* Empty state vede jednou větou + jedním tlačítkem; „jak to funguje" za „?"
          (blueprint 16 §5 — dlouhý vysvětlovací text pryč z plátna). */}
      {dodatky && dodatky.length === 0 && (
        <EmptyState
          ikona={<FileText size={22} strokeWidth={1.75} />}
          titulek={filtr ? 'Vybraná zakázka nemá dodatky' : 'Zatím žádný dodatek'}
          popis="Dodatek sestavíte z podepsaných změnových listů jedné smlouvy."
          akce={
            <div className="flex items-center gap-2">
              {filtr && (
                <button
                  type="button"
                  className="font-semibold text-primary underline underline-offset-2"
                  onClick={() => setVsechnyZakazky(true)}
                >
                  Zobrazit všechny zakázky
                </button>
              )}
              {maPravo('finance.write') && (
                <Btn size="desktop" onClick={() => setZakladam(true)}>
                  Sestavit první
                </Btn>
              )}
              <Napoveda titulek="Jak dodatek funguje">
                <p>
                  Dodatek je obálka nad podepsanými výnosovými změnovými listy (ZL) jedné smlouvy —
                  spouštěčem bývá finanční objem. Podpisem dodatku se změny stanou fakturovatelné.
                </p>
              </Napoveda>
            </div>
          }
        />
      )}

      {dodatky && dodatky.length > 0 && (
        <Karta>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={th}>Číslo</th>
                  <th className={th}>Smlouva</th>
                  <th className={th}>ZL</th>
                  <th className={`${th} text-right`}>Σ (netto)</th>
                  <th className={th}>Stav</th>
                  <th className={th}>Podepsán</th>
                </tr>
              </thead>
              <tbody>
                {dodatky.map((d) => (
                  <tr
                    key={d.id}
                    className="cursor-pointer hover:bg-bg"
                    onClick={() => void navigateSeznam(`/dodatky/${d.id}`)}
                  >
                    <td className={`${td} font-semibold`}>
                      <Link
                        to={`/dodatky/${d.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary underline underline-offset-2"
                      >
                        {d.cislo ?? d.id}
                      </Link>
                    </td>
                    <td className={td}>
                      {d.smlouva ? `${d.smlouva.cislo ?? ''} — ${d.smlouva.protistrana}` : '—'}
                    </td>
                    <td className={td}>{d.zmenoveListy.length}</td>
                    <td className={`${td} text-right whitespace-nowrap`}>{formatKc(d.castka)}</td>
                    <td className={td}>
                      <Badge stav={d.podepsano ? 'success' : 'info'}>
                        {d.podepsano ? 'podepsaný' : 'nepodepsaný'}
                      </Badge>
                    </td>
                    <td className={td}>{formatDatum(d.datumPodpisu)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtr && (
              <button
                type="button"
                className="mt-2 text-sm font-semibold text-primary underline underline-offset-2"
                onClick={() => setVsechnyZakazky(true)}
              >
                Zobrazit dodatky všech zakázek
              </button>
            )}
          </div>
        </Karta>
      )}

      {zakladam && (
        <NovyDodatekDialog
          initialZakazkaId={initialZakazkaId}
          initialZlId={initialZlId}
          onZavrit={zavri}
        />
      )}
    </>
  );
}

/**
 * Sestavení balíku dodatku: výběr podepsaných výnosových ZL hlavní SOD zakázky
 * (checkboxy) se Σ vybraných — závaznou Σ počítá server.
 */
function NovyDodatekDialog({
  initialZakazkaId = '',
  initialZlId = '',
  onZavrit,
}: {
  initialZakazkaId?: string;
  initialZlId?: string;
  onZavrit: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [zakazkaId, setZakazkaId] = useState(initialZakazkaId);
  const [vybrane, setVybrane] = useState<Set<string>>(
    () => new Set(initialZlId ? [initialZlId] : []),
  );
  const [chyba, setChyba] = useState<string | null>(null);

  const { data: zakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  const vybranaZakazka = zakazkaId || zakazky?.[0]?.id || '';

  const { data: smlouvy } = useQuery({
    queryKey: ['smlouvy', vybranaZakazka],
    queryFn: () => api.get<Smlouva[]>(`/api/contracts?zakazkaId=${vybranaZakazka}`),
    enabled: !!vybranaZakazka,
  });
  // Dodatek balí ZL JEDNÉ smlouvy: hlavní SOD → výnosové ZL (investor),
  // subsmlouva → nákladové ZL té subky (roste i základ její pozastávky).
  const [smlouvaId, setSmlouvaId] = useState('');
  const smlouva =
    smlouvy?.find((s) => s.id === smlouvaId) ?? smlouvy?.find((s) => s.typ === 'hlavni');
  const ocekavanySmer = smlouva?.typ === 'hlavni' ? 'vynos' : 'naklad';

  const { data: zmeny, isLoading } = useQuery({
    queryKey: ['zmeny', `zakazkaId=${vybranaZakazka}`],
    queryFn: () => api.get<ZmenaSeznam[]>(`/api/zmeny?zakazkaId=${vybranaZakazka}`),
    enabled: !!vybranaZakazka,
  });

  const kandidati = useMemo(
    () =>
      (zmeny ?? []).flatMap((z) =>
        z.zmenoveListy
          .filter(
            (zl) =>
              zl.smer === ocekavanySmer &&
              zl.stav === 'podepsano' &&
              !zl.dodatekId &&
              zl.smlouvaId === smlouva?.id,
          )
          .map((zl) => ({ ...zl, zmena: z })),
      ),
    [zmeny, ocekavanySmer, smlouva?.id],
  );
  const sumaVybranych = souctiCastky(
    kandidati.filter((k) => vybrane.has(k.id)).map((k) => k.castka),
  );

  const zalozit = useMutation({
    mutationFn: () =>
      api.post<Dodatek>('/api/dodatky', {
        smlouvaId: smlouva?.id,
        zlIds: [...vybrane],
      }),
    onSuccess: (dodatek) => {
      void queryClient.invalidateQueries({ queryKey: ['dodatky'] });
      void navigate(`/dodatky/${dodatek.id}`);
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  return (
    <Dialog nadpis="Nový dodatek" siroky onZavrit={onZavrit}>
      <div className="flex flex-col gap-4">
        {chyba && <Chyba>{chyba}</Chyba>}
        <label className="text-sm font-semibold text-text-secondary">
          Zakázka
          <Select
            className="mt-1 w-72"
            value={vybranaZakazka}
            onChange={(e) => {
              setZakazkaId(e.target.value);
              setVybrane(new Set());
            }}
          >
            {(zakazky ?? []).map((z) => (
              <option key={z.id} value={z.id}>
                {z.nazev}
              </option>
            ))}
          </Select>
        </label>
        <label className="text-sm font-semibold text-text-secondary">
          Smlouva dodatku
          <Select
            className="mt-1 w-full max-w-md"
            value={smlouva?.id ?? ''}
            onChange={(e) => {
              setSmlouvaId(e.target.value);
              setVybrane(new Set());
            }}
          >
            {(smlouvy ?? []).map((sm) => (
              <option key={sm.id} value={sm.id}>
                {sm.cislo ?? '(bez čísla)'} — {sm.protistrana} (
                {sm.typ === 'hlavni' ? 'hlavní SOD' : 'subdodavatelská'})
              </option>
            ))}
          </Select>
        </label>
        {smlouva && smlouva.typ !== 'hlavni' && (
          <p className="text-sm text-text-secondary">
            Dodatek k subsmlouvě balí podepsané NÁKLADOVÉ ZL této subky — podpisem se navýší cena
            subsmlouvy i základ pozastávky, kterou subce držíme.
          </p>
        )}
        {!smlouva && smlouvy && <Chyba>Zakázka nemá žádnou smlouvu — dodatek nejde založit.</Chyba>}

        {isLoading && <Nacitani text="Načítám podepsané výnosové ZL…" />}
        {zmeny && kandidati.length === 0 && (
          <p className="text-text-secondary">
            Žádný podepsaný výnosový ZL k zařazení — do dodatku patří jen podepsané výnosové ZL,
            které ještě nejsou v jiném dodatku.
          </p>
        )}
        {kandidati.length > 0 && (
          <ul className="flex flex-col gap-1">
            {kandidati.map((k) => (
              <li key={k.id}>
                <label className="flex min-h-11 items-center gap-3 rounded-lg px-2 hover:bg-bg">
                  <input
                    type="checkbox"
                    className="size-5"
                    checked={vybrane.has(k.id)}
                    onChange={(e) =>
                      setVybrane((s) => {
                        const nove = new Set(s);
                        if (e.target.checked) nove.add(k.id);
                        else nove.delete(k.id);
                        return nove;
                      })
                    }
                  />
                  <span className="flex-1">
                    <span className="font-semibold">{k.cislo ?? k.id}</span> · Změna{' '}
                    {k.zmena.cislo ?? ''} — {k.zmena.popis} ({ZMENA_STAV_LABEL[k.zmena.stav]})
                  </span>
                  <span className="font-semibold whitespace-nowrap">{formatKc(k.castka)}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <p className="text-right font-bold">
          Σ vybraných ZL: {formatKc(sumaVybranych)}{' '}
          <span className="text-xs font-normal text-text-secondary">(netto, bez DPH)</span>
        </p>
        <div className="flex gap-3">
          <Btn
            size="desktop"
            disabled={zalozit.isPending || vybrane.size === 0 || !smlouva}
            onClick={() => zalozit.mutate()}
          >
            {zalozit.isPending ? 'Zakládám…' : `Sestavit dodatek (${vybrane.size} ZL)`}
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={onZavrit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}

/** Detail dodatku se seznamem ZL a podpisem (jediná brána k fakturaci). */
export function DodatekDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { me, maPravo } = useAuth();
  const [podepisuji, setPodepisuji] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);

  const {
    data: dodatek,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dodatek', id],
    queryFn: () => api.get<Dodatek>(`/api/dodatky/${id}`),
  });

  const [podpisObrazek, setPodpisObrazek] = useState<string | null>(null);
  const [odkazProtistrany, setOdkazProtistrany] = useState(false);
  const podepsat = useMutation({
    mutationFn: () =>
      api.post<Dodatek>(`/api/dodatky/${id}/podepsat`, {
        podpis: podpisObrazek ? { obrazek: podpisObrazek, jmeno: me?.jmeno ?? '' } : undefined,
      }),
    onSuccess: () => {
      setPodepisuji(false);
      setChyba(null);
      void queryClient.invalidateQueries({ queryKey: ['dodatek', id] });
      void queryClient.invalidateQueries({ queryKey: ['dodatky'] });
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  if (isLoading) return <Nacitani text="Načítám dodatek…" />;
  if (error) return <Chyba>{hlaskaZChyby(error)}</Chyba>;
  if (!dodatek) return null;

  const pocetZmen = new Set(dodatek.zmenoveListy.map((zl) => zl.zmenaId)).size;

  const zmenSklon = pocetZmen === 1 ? 'změna' : pocetZmen < 5 ? 'změny' : 'změn';

  return (
    <>
      {/* Masthead: titul vlevo, STAV jako razítko vpravo nahoře. */}
      <header className="flex items-start justify-between gap-4 border-b border-border pb-4">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-text-secondary">
            <Link to="/dodatky" className="underline underline-offset-2">
              Dodatky
            </Link>{' '}
            / {dodatek.cislo ?? dodatek.id}
          </div>
          <h1 className="mt-1 text-2xl font-bold">
            Dodatek {dodatek.cislo ?? ''}
            {dodatek.smlouva ? ` — ${dodatek.smlouva.protistrana}` : ''}
          </h1>
        </div>
        <div
          className={`shrink-0 rounded-xl px-4 py-2 text-center ${
            dodatek.podepsano ? 'bg-success-bg text-success-text' : 'bg-info-bg text-info-text'
          }`}
        >
          <p className="text-xs font-bold tracking-wide uppercase opacity-80">Stav</p>
          <p className="text-lg font-bold">{dodatek.podepsano ? 'podepsaný' : 'nepodepsaný'}</p>
          {dodatek.podepsano && <p className="text-xs">{formatDatum(dodatek.datumPodpisu)}</p>}
        </div>
      </header>

      {chyba && <Chyba>{chyba}</Chyba>}

      {/* Hero: částka dodatku = headline číslo + primární akce podpisu. */}
      <section className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-surface p-6 shadow-sm">
        <div className="min-w-0">
          <p className="text-xs font-bold tracking-wide text-text-secondary uppercase">
            Σ dodatku (netto, bez DPH)
          </p>
          <p className="text-3xl font-bold text-text-heading">{formatKc(dodatek.castka)}</p>
          <p className="mt-1 text-sm text-text-secondary">
            {pocetZmen} {zmenSklon} · {dodatek.zmenoveListy.length} ZL
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`/api/dodatky/${dodatek.id}/pdf`}
            download
            className="text-sm font-semibold text-primary underline underline-offset-2"
          >
            Stáhnout PDF
          </a>
        </div>
        {!dodatek.podepsano && maPravo('finance.write') && (
          <div className="flex gap-2">
            <Btn size="desktop" onClick={() => setPodepisuji(true)}>
              Podepsat dodatek
            </Btn>
            <Btn size="desktop" varianta="secondary" onClick={() => setOdkazProtistrany(true)}>
              Podpis protistrany
            </Btn>
          </div>
        )}
      </section>

      <Karta nadpis={`Změnové listy dodatku (${dodatek.zmenoveListy.length})`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className={th}>ZL</th>
                <th className={th}>Změna</th>
                <th className={th}>Popis</th>
                <th className={`${th} text-right`}>Částka (netto)</th>
              </tr>
            </thead>
            <tbody>
              {dodatek.zmenoveListy.map((zl) => (
                <tr key={zl.id}>
                  <td className={`${td} font-semibold`}>{zl.cislo ?? zl.id}</td>
                  <td className={td}>
                    <Link
                      to={`/zmeny/${zl.zmenaId}`}
                      className="text-primary underline underline-offset-2"
                    >
                      {zl.zmena.cislo ?? ''}
                    </Link>{' '}
                    ({ZMENA_STAV_LABEL[zl.zmena.stav]})
                  </td>
                  <td className={td}>{zl.popis}</td>
                  <td className={`${td} text-right whitespace-nowrap`}>{formatKc(zl.castka)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-bold">
                <td className={td} colSpan={3}>
                  Σ dodatku (netto, bez DPH)
                </td>
                <td className={`${td} text-right whitespace-nowrap`}>{formatKc(dodatek.castka)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Karta>

      {odkazProtistrany && (
        <PodpisProtistranyDialog
          entita="Dodatek"
          entitaId={dodatek.id}
          nazevDokumentu={`Dodatek ${dodatek.cislo ?? ''}`}
          jmeno={dodatek.smlouva?.protistrana}
          onZavrit={() => setOdkazProtistrany(false)}
        />
      )}
      {podepisuji && (
        <Dialog nadpis="Podepsat dodatek" onZavrit={() => setPodepisuji(false)}>
          <div className="flex flex-col gap-4">
            <p>
              Podpis dodatku převede {pocetZmen}{' '}
              {pocetZmen === 1 ? 'změnu' : pocetZmen < 5 ? 'změny' : 'změn'} do stavu fakturovatelná
              a navýší cenu Díla o {formatKc(dodatek.castka)}; přepočte pozastávky (neuvolněné
              tranše z nového základu).
            </p>
            <PodpisVstup
              entita="Dodatek"
              entitaId={dodatek.id}
              jmeno={me?.jmeno ?? ''}
              onChange={setPodpisObrazek}
            />
            <p className="-mt-2 text-sm text-text-secondary">Podepisuje: {me?.jmeno}</p>
            <div className="flex gap-3">
              <Btn
                size="desktop"
                disabled={podepsat.isPending || !podpisObrazek}
                onClick={() => podepsat.mutate()}
              >
                {podepsat.isPending
                  ? 'Podepisuji…'
                  : `Potvrdit podpis (+${formatKc(dodatek.castka)})`}
              </Btn>
              <Btn size="desktop" varianta="secondary" onClick={() => setPodepisuji(false)}>
                Zrušit
              </Btn>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}
